import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AccordionUi from 'Frontend/components/AccordionUi';
import {ModuleMargin} from 'Frontend/components/ModuleMargins';
import AccordionVideo from 'Frontend/components/modules/AccordionVideo';
import TabContainer from 'Frontend/components/modules/TabContainer';
import Tabs from 'Frontend/components/modules/Tabs';

import {parseToUri} from 'Admin/utils/formParsers';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA, MEDIA_JS} from 'Frontend/utils/mediaQueries';
import {isValidNode, processingInstructions} from 'Frontend/utils/parserInstructions';

/**
 * VideoTab
 *
 * @property {*} prop Prop comment.
 *
 * @class VideoTab
 * @extends {Component}
 */
class VideoTab extends Component {
    static propTypes = {
        position: PropTypes.string.isRequired,
        tabs: PropTypes.array.isRequired,
        copy: PropTypes.string,
        headline: PropTypes.string,
        isInGroup: PropTypes.bool,
        name: PropTypes.string
    }

    static defaultProps = {
        copy: null,
        headline: null,
        isInGroup: false,
        name: null
    }

    htmlParser = new Parser();

    /**
     * Creates an instance of VideoTab.
     *
     * @param {Object} props Component props.
     * @memberof VideoTab
     */
    constructor(props) {
        super(props);

        if (process.browser) {
            this.mobile = window.matchMedia(MEDIA_JS.MOBILE.DOWN);
        }

        this.state = {
            activeTab: 0,
            viewport: 'Desktop'
        };
    }

    /**
     * Add listener to viewport changes.
     *
     * @memberof ImageModule
     */
    componentDidMount() {
        if ('addEventListener' in this.mobile) {
            this.mobile.addEventListener('change', this.setViewport);
        } else {
            this.mobile.addListener(this.setViewport);
        }

        this.setViewport();
    }

    /**
     * Remove listener to viewport changes.
     *
     * @memberof ImageModule
     */
    componentWillUnmount() {
        if ('removeEventListener' in this.mobile) {
            this.mobile.removeEventListener('change', this.setViewport);
        } else {
            this.mobile.removeListener(this.setViewport);
        }
    }

    /**
     * Sets the viewport in state.
     *
     * @memberof ImageModule
     */
    @autobind
    setViewport() {
        this.setState({viewport: this.getViewport()});
    }

    /**
     * Gets the viewport from the mediaQuerySelector.
     *
     * @returns {String} The current viewport.
     * @memberof ImageModule
     */
    @autobind
    getViewport() {
        if (this.mobile.matches) {
            return 'Mobile';
        }

        return 'Desktop';
    }

    /**
     * Handles the active tab change.
     *
     * @param {Number} index The index of the active tab.
     * @memberof VideoTab
     */
    @autobind
    handleTabChange(index) {
        this.setState({activeTab: index});
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof VideoTab
     */
    render() {
        const {activeTab, viewport} = this.state;
        const {copy, headline, isInGroup, name, position, tabs} = this.props;

        return (
            <VideoTabElement isInGroup={isInGroup} position={position}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={parseToUri(name)} />
                {(headline || copy) && (
                    <VideoTabContainer>
                        {headline && (
                            <Headline>{headline}</Headline>
                        )}
                        {copy && (
                            <Text>
                                {this.htmlParser.parseWithInstructions(
                                    `<div data-module="VideoTab">${copy}</div>`,
                                    isValidNode,
                                    processingInstructions
                                )}
                            </Text>
                        )}
                    </VideoTabContainer>
                )}
                {(viewport === 'Desktop') && (
                    <>
                        <Tabs
                            active={activeTab}
                            tabNames={tabs.map(item => item.name)}
                            onClick={this.handleTabChange}
                        />
                        <TabContainer
                            active={activeTab}
                            position={position}
                            tabContents={tabs.map(item => item.content)}
                        />
                    </>
                )}
                {(viewport === 'Mobile') && tabs.map(tab => (
                    <AccordionContainer key={tab.name}>
                        <AccordionUi
                            accordionBodyComponent={<AccordionVideo contents={tab.content} />}
                            accordionName={tab.name}
                        />
                    </AccordionContainer>
                ))}
            </VideoTabElement>
        );
    }
}

export default VideoTab;

const VideoTabElement = styled(ModuleMargin)`
    align-items: center;
    display: flex;
    flex-direction: column;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.DESKTOP.UP} {
        max-width: 100%;
        padding: 0;
        width: ${({isInGroup}) => (isInGroup ? '100%' : `max(1320px, ${pxToVw(1320, 'd')})`)};
    }
`;

const VideoTabContainer = styled.div`
    margin-bottom: ${clampCss(32, 'm')};
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.TABLET.UP} {
        margin-bottom: ${clampCss(80, 't')};
        width: 66.66%;
    }

    ${MEDIA.DESKTOP.UP} {
        margin-bottom: max(80px, ${pxToVw(80, 'd')});
    }
`;

const Headline = styled.h3`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(20, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(24, 'm')};
    margin: 0;
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(20, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(24, 't')};
        text-align: center;
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(20px, ${pxToVw(20, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(24px, ${pxToVw(24, 'd')});
        text-align: center;
    }
`;

const Text = styled.div`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        text-align: center;
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        text-align: center;
    }
`;

const AccordionContainer = styled.div`
    width: 100%;
`;